import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { postsApi } from "@/api";
import type { CreatePostData, PostFilter, UpdatePostData } from "@/api";
import { QUERY_KEYS } from "@/constants";

// ─── Ommaviy ──────────────────────────────────────────────────────────────────

/** `enabled` — sahifa bir nechta ko'rinishdan faqat faolini yuklasin. */
export const useNews = (filter: PostFilter, enabled = true) =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_NEWS, filter],
    queryFn: () => postsApi.listNews(filter),
    placeholderData: keepPreviousData,
    enabled,
    select: res => ({ rows: res.data, meta: res.meta }),
  });

export const useNewsArticle = (slug: string | undefined) =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_NEWS_ONE, slug],
    queryFn: () => postsApi.getNews(slug as string),
    enabled: !!slug,
    select: res => res.data,
  });

export const useBlogs = (filter: PostFilter) =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_BLOGS, filter],
    queryFn: () => postsApi.listBlogs(filter),
    placeholderData: keepPreviousData,
    select: res => ({ rows: res.data, meta: res.meta }),
  });

export const useBlogArticle = (slug: string | undefined) =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_BLOG_ONE, slug],
    queryFn: () => postsApi.getBlog(slug as string),
    enabled: !!slug,
    select: res => res.data,
  });

export const usePostCategories = (type: "NEWS" | "BLOG") =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_POST_CATEGORIES, type],
    queryFn: () => (type === "NEWS" ? postsApi.newsCategories() : postsApi.blogCategories()),
    select: res => res.data,
  });

// ─── Muallif ────────────────────────────────────────────────────────────────

export const useMyPosts = (filter: PostFilter, enabled = true) =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_MY_POSTS, filter],
    queryFn: () => postsApi.listMine(filter),
    placeholderData: keepPreviousData,
    enabled,
    select: res => ({ rows: res.data, meta: res.meta }),
  });

export const usePost = (id: number | undefined) =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_POST, id],
    queryFn: () => postsApi.getOne(id as number),
    enabled: !!id,
    select: res => res.data,
  });

/** Postga aloqador hamma so'rovni bekor qiladi (keng, lekin sodda). */
const useInvalidatePosts = () => {
  const queryClient = useQueryClient();
  return () => {
    [
      QUERY_KEYS.GET_MY_POSTS,
      QUERY_KEYS.GET_POST,
      QUERY_KEYS.GET_NEWS,
      QUERY_KEYS.GET_BLOGS,
      QUERY_KEYS.GET_POST_MODERATION_LIST,
      QUERY_KEYS.GET_POST_MODERATION_STATS,
    ].forEach(key => queryClient.invalidateQueries({ queryKey: [key] }));
  };
};

export const useCreatePost = () => {
  const { t } = useTranslation();
  const invalidate = useInvalidatePosts();
  return useMutation({
    mutationFn: (data: CreatePostData) => postsApi.create(data),
    onSuccess: () => {
      invalidate();
      toast.success(t("COMMON.ADDED"));
    },
  });
};

export const useUpdatePost = () => {
  const { t } = useTranslation();
  const invalidate = useInvalidatePosts();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdatePostData }) => postsApi.update(id, data),
    onSuccess: () => {
      invalidate();
      toast.success(t("COMMON.UPDATED"));
    },
  });
};

export const useDeletePost = () => {
  const { t } = useTranslation();
  const invalidate = useInvalidatePosts();
  return useMutation({
    mutationFn: (id: number) => postsApi.remove(id),
    onSuccess: () => {
      invalidate();
      toast.success(t("COMMON.DELETED"));
    },
  });
};

export const useSubmitPost = () => {
  const { t } = useTranslation();
  const invalidate = useInvalidatePosts();
  return useMutation({
    mutationFn: (id: number) => postsApi.submit(id),
    onSuccess: () => {
      invalidate();
      toast.success(t("POSTS.SUBMITTED_TOAST"));
    },
  });
};

// ─── Xodimlar ─────────────────────────────────────────────────────────────────

export const usePublishPost = () => {
  const { t } = useTranslation();
  const invalidate = useInvalidatePosts();
  return useMutation({
    mutationFn: (id: number) => postsApi.publish(id),
    onSuccess: () => {
      invalidate();
      toast.success(t("POSTS.PUBLISHED_TOAST"));
    },
  });
};

export const useUnpublishPost = () => {
  const { t } = useTranslation();
  const invalidate = useInvalidatePosts();
  return useMutation({
    mutationFn: (id: number) => postsApi.unpublish(id),
    onSuccess: () => {
      invalidate();
      toast.success(t("POSTS.UNPUBLISHED_TOAST"));
    },
  });
};

export const useApprovePost = () => {
  const { t } = useTranslation();
  const invalidate = useInvalidatePosts();
  return useMutation({
    mutationFn: (id: number) => postsApi.approve(id),
    onSuccess: () => {
      invalidate();
      toast.success(t("POSTS.APPROVED_TOAST"));
    },
  });
};

export const useRejectPost = () => {
  const { t } = useTranslation();
  const invalidate = useInvalidatePosts();
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) => postsApi.reject(id, reason),
    onSuccess: () => {
      invalidate();
      toast.success(t("POSTS.REJECTED_TOAST"));
    },
  });
};

export const usePostModerationStats = () =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_POST_MODERATION_STATS],
    queryFn: () => postsApi.moderationStats(),
    select: res => res.data,
  });

export const usePostModerationList = (filter: PostFilter) =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_POST_MODERATION_LIST, filter],
    queryFn: () => postsApi.moderationList(filter),
    placeholderData: keepPreviousData,
    select: res => ({ rows: res.data, meta: res.meta }),
  });

// ─── Izohlar va layklar ─────────────────────────────────────────────────────

export const usePostComments = (postId: number | undefined, page: number) =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_POST_COMMENTS, postId, page],
    queryFn: () => postsApi.listComments(postId as number, { page, limit: 10 }),
    enabled: !!postId,
    placeholderData: keepPreviousData,
    select: res => ({ rows: res.data, meta: res.meta }),
  });

export const useAddComment = (postId: number) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (content: string) => postsApi.addComment(postId, content),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_POST_COMMENTS, postId] });
      toast.success(t("COMMENTS.ADDED_TOAST"));
    },
  });
};

export const useDeleteComment = (postId: number) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (commentId: number) => postsApi.deleteComment(commentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_POST_COMMENTS, postId] });
      toast.success(t("COMMENTS.DELETED_TOAST"));
    },
  });
};

export const usePostLikeState = (postId: number | undefined, enabled = true) =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_POST_LIKES, postId],
    queryFn: () => postsApi.likeState(postId as number),
    enabled: !!postId && enabled,
    select: res => res.data,
  });

export const useToggleLike = (postId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => postsApi.toggleLike(postId),
    onSuccess: res => {
      queryClient.setQueryData([QUERY_KEYS.GET_POST_LIKES, postId], {
        success: true,
        message: null,
        data: res.data,
        timestamp: new Date().toISOString(),
      });
    },
  });
};
